// The birthday card's age + by-the-year table (profile spec §3.5, §2.2 P2/P10).
//
// Three things this gets right that are easy to get wrong:
//
// * **The number is "今年 N 歲"**, the age reached *this calendar year*, so it
//   does not flip on the birthday itself — profile spec §2.1 lists both
//   phrasings the card may use and they carry the same N.
// * **The table starts at the year heshe was joined**, not the birth year.
//   A 30-year-old who joined in 2026 sees one row, not thirty. Profile spec
//   §2.2 P10 is explicit about this and it is the difference between a warm
//   detail and a scroll.
// * **It stops at the current year.** The spec says "only years that have
//   happened"; listing 2030 in 2026 is a preview, not a memory.
//
// `joinedAt` is the member's `User.created_at`. The API does not send it on
// the anniversary row, so the caller reads it from the profile it already
// fetched; when it is unknown the table falls back to the current year rather
// than guessing a start.

import { useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { cn } from "@/lib/utils/cn";
import { ageReaching, birthdayYearTable, daysUntilBirthday } from "@/lib/profile/zodiac";

export interface BirthdayAgeCardProps {
  /** The member's birth date, `YYYY-MM-DD`. */
  birthday: string | null;
  /** The member's `User.created_at`; the table's first row. */
  joinedAt?: string | null;
  today?: Date;
}

export function BirthdayAgeCard({ birthday, joinedAt, today = new Date() }: BirthdayAgeCardProps) {
  const [open, setOpen] = useState(false);

  if (!birthday) return null;

  const age = ageReaching(birthday, today.getFullYear());
  const days = daysUntilBirthday(birthday, today);
  const rows = birthdayYearTable(birthday, joinedAt, today);
  const hadBirthday =
    today.getMonth() + 1 > Number(birthday.slice(5, 7)) ||
    (today.getMonth() + 1 === Number(birthday.slice(5, 7)) &&
      today.getDate() >= Number(birthday.slice(8, 10)));

  return (
    <section
      data-component="BirthdayAgeCard"
      data-testid="birthday-age-card"
      className="flex flex-col gap-s-2"
    >
      <p className="text-body-sm text-ink-2" data-testid="birthday-age-line">
        <span className="font-semibold text-ink-1">
          {age === null ? "" : hadBirthday ? `今年 ${age} 歲` : `今年將滿 ${age} 歲`}
        </span>
        {days !== null ? (
          <span className="ml-s-2 tabular-nums text-ink-3">距今年生日 {days} 天</span>
        ) : null}
      </p>

      {rows.length > 0 ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            data-testid="birthday-table-toggle"
            className="tap-target min-h-11 min-w-11 flex items-center gap-s-1 self-start text-body-sm text-ink-2 hover:bg-black/5"
          >
            {open ? (
              <ChevronUp size={16} strokeWidth={1.5} aria-hidden="true" />
            ) : (
              <ChevronDown size={16} strokeWidth={1.5} aria-hidden="true" />
            )}
            {open ? "收起年表" : `加入 heshe 之後的 ${rows.length} 年`}
          </button>

          {open ? (
            <ol
              className="flex flex-col gap-s-1"
              data-testid="birthday-year-table"
              aria-label="歷年年表"
            >
              {rows.map((row) => (
                <li
                  key={row.year}
                  className={cn(
                    "flex items-baseline justify-between rounded-xs px-s-2 py-s-1 text-body-sm tabular-nums",
                    row.isCurrent ? "bg-you-tint font-semibold text-you-strong" : "text-ink-2",
                  )}
                  data-testid={`birthday-year-${row.year}`}
                  data-current={row.isCurrent || undefined}
                >
                  <span>{row.year}</span>
                  <span>{row.age} 歲</span>
                </li>
              ))}
            </ol>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
